import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import Swal from 'sweetalert2';
import html2pdf from 'html2pdf.js';
import {
    RiEyeLine,
    RiDownload2Line,
    RiSendPlaneLine,
    RiLoader4Line,
    RiFileTextLine,
    RiCloseLine
} from 'react-icons/ri';
import API_BASE_URL from '../../../config/api';
import Topbar from '../mini-comp/Topbar';
import Sidebar from '../mini-comp/Sidebar';

const MyPayslips = () => {
    const [payslips, setPayslips] = useState([]);
    const [selectedPayslip, setSelectedPayslip] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const payslipRef = useRef();

    useEffect(() => {
        const fetchPayslips = async () => {
            try {
                const token = Cookies.get('token');
                const res = await axios.get(`${API_BASE_URL}/api/v1/payroll/my-payslips`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                setPayslips(res.data || []);
            } catch (err) {
                console.error("Error fetching payslips:", err);
                setError("Failed to load payslips.");
            } finally {
                setLoading(false);
            }
        };
        fetchPayslips();
    }, []);

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
    };

    const formatDate = (dateString) => {
        if (!dateString) return '---';
        return new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    };

    const handleViewPayslip = (payslip) => {
        setSelectedPayslip(payslip);
        setShowModal(true);
    };

    const handleDownloadPDF = () => {
        const element = payslipRef.current;
        const opt = {
            margin: 0.5,
            filename: `Payslip_${new Date(selectedPayslip.generated_on).getFullYear()}_${new Date(selectedPayslip.generated_on).getMonth() + 1}.pdf`,
            image: { type: 'jpeg', quality: 0.98 },
            html2canvas: { scale: 2 },
            jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' }
        };

        Swal.fire({
            title: 'Generating PDF...',
            allowOutsideClick: false,
            didOpen: () => {
                Swal.showLoading();
                html2pdf().set(opt).from(element).save().then(() => {
                    Swal.fire({
                        icon: 'success',
                        title: 'Downloaded!',
                        text: 'Your payslip has been downloaded successfully.',
                        timer: 2000,
                        showConfirmButton: false
                    });
                });
            }
        });
    };

    if (loading) return (
        <div>
            <Topbar /> <Sidebar />
            <div className="app-content-area text-center py-5">
                <RiLoader4Line className="spinner-border text-primary fs-1 mb-3" style={{ border: 'none', animation: 'spin 1s linear infinite' }} />
                <p>Loading your payslips...</p>
                <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
            </div>
        </div>
    );

    return (
        <div>
            <Topbar />
            <Sidebar />
            <div className="app-content-area">
                <div className="app-content-wrap">
                    <div className="container-fluid">
                        <div className="row">
                            <div className="col-xl-12">
                                <div className="card shadow-sm border-0">
                                    <div className="card-body p-0">
                                        {error ? (
                                            <div className="alert alert-danger m-4">{error}</div>
                                        ) : payslips.length === 0 ? (
                                            <div className="text-center py-5">
                                                <RiFileTextLine size={50} className="text-muted mb-3" />
                                                <h4>No Payslips Found</h4>
                                                <p className="text-muted">You don't have any generated payslips yet.</p>
                                            </div>
                                        ) : (
                                            <div className="table-responsive">
                                                <h1 className="page-title fs-18 lh-1 d-flex align-items-center gap-10 mb-5">
                                                    <RiFileTextLine className="text-primary" /> My Payslips
                                                </h1>
                                                <hr />
                                                <br />
                                                <table className="table table-hover align-middle mb-0">
                                                    <thead className="bg-light-subtle text-dark">
                                                        <tr>
                                                            <th className="ps-4">Pay Period</th>
                                                            <th>Gross Salary</th>
                                                            <th>Basic Salary</th>
                                                            <th>Total Allowances</th>
                                                            <th>Total Deductions</th>
                                                            <th>Net Pay</th>
                                                            <th>Generation Date</th>
                                                            <th className="text-end pe-4">Action</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {payslips.map((p) => (
                                                            <tr key={p.id}>
                                                                <td className="ps-4 fw-bold text-primary">
                                                                    {new Date(p.generated_on).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                                                                </td>
                                                                <td>{formatCurrency(p.gross_salary)}</td>
                                                                <td>{formatCurrency(p.employee_data.basic_salary)}</td>
                                                                <td>{formatCurrency(p.total_allowances)}</td>
                                                                <td>{formatCurrency(p.total_deductions)}</td>
                                                                <td className="fw-bold text-success">{formatCurrency(p.net_salary)}</td>
                                                                <td className="text-muted fs-13">{formatDate(p.generated_on)}</td>
                                                                <td className="text-end pe-4">
                                                                    <button
                                                                        className="btn btn-sm btn-outline-primary d-inline-flex align-items-center gap-2"
                                                                        onClick={() => handleViewPayslip(p)}
                                                                    >
                                                                        <RiEyeLine size={14} /> View Details
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Payslip Detail Modal */}
            {showModal && selectedPayslip && (
                <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(37, 36, 36, 0.5)', zIndex: 1060 }}>
                    <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
                        <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '12px' }}>
                            <div className="modal-header bg-dark text-white border-0 px-4 py-3">
                                <h5 className="modal-title fw-bold d-flex align-items-center gap-2 fs-16">
                                    <RiFileTextLine className="text-info" /> Payslip View - {new Date(selectedPayslip.generated_on).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                                </h5>

                            </div>
                            <div className="modal-body p-0 bg-light">
                                <div className="p-4">
                                    {/* This is the element that will be converted to PDF */}
                                    <div ref={payslipRef} className="printable-payslip">
                                        <div className="printable-watermark">
                                            <img src="/assets/images/burray/burray.png" alt="logo" className="logo-watermark-img" />
                                        </div>
                                        <div className="printable-header">
                                            <div className="company-name">{selectedPayslip?.employee_data?.company || 'BURRAY TECHNOLOGIES'}</div>
                                        </div>

                                        <div className="printable-section">
                                            <div className="info-grid">
                                                <div><strong>Employee Code:</strong> {selectedPayslip?.employee_data?.employee_id || 'N/A'}</div>
                                                <div><strong>Surname:</strong> {selectedPayslip?.employee_data?.last_name || 'N/A'}</div>
                                                <div><strong>First Name:</strong> {selectedPayslip?.employee_data?.first_name || 'N/A'}</div>
                                                <div><strong>Job Title:</strong> {selectedPayslip?.employee_data?.position || 'N/A'}</div>
                                                <div><strong>SSR No.:</strong> {selectedPayslip?.employee_data?.nssa_ssr_number || 'N/A'}</div>
                                                <div><strong>TIN No.:</strong> {selectedPayslip?.employee_data?.zimra_tin_number || 'N/A'}</div>
                                                <div><strong>Date Employed:</strong> {selectedPayslip?.employee_data?.date_employed || 'N/A'}</div>
                                                <div><strong>Payroll Date:</strong> {formatDate(selectedPayslip?.generated_on)}</div>
                                                <div><strong>Department:</strong> {selectedPayslip?.employee_data?.department || 'N/A'}</div>
                                                <div><strong>Leave Balance:</strong> {selectedPayslip?.employee_data?.vacation_leave_days_available || 'N/A'}</div>
                                                <div><strong>Bank Name:</strong> {selectedPayslip?.bank_name || 'N/A'}</div>
                                                <div><strong>Bank Branch:</strong> {selectedPayslip?.bank_branch || 'N/A'}</div>
                                                <div><strong>Account No:</strong> {selectedPayslip?.bank_account_number || 'N/A'}</div>
                                            </div>
                                        </div>

                                        <div className="printable-section earnings-deductions-grid">
                                            <div className="earnings-col">
                                                <div className="section-title">Earnings</div>
                                                <div className="item-list">
                                                    <div className="list-item"><span>Basic Salary</span><span>{formatCurrency(selectedPayslip?.employee_data?.basic_salary)}</span></div>
                                                    {selectedPayslip?.allowance_data?.map((item, idx) => (
                                                        <div key={idx} className="list-item">
                                                            <span>{item.title}</span>
                                                            <span>{formatCurrency(item.amount)}</span>
                                                        </div>
                                                    ))}
                                                    {selectedPayslip?.overtime_1_5 > 0 && (
                                                        <div className="list-item">
                                                            <span>Overtime(1.5x)</span>
                                                            <span>{formatCurrency(selectedPayslip?.overtime_1_5)}</span>
                                                        </div>
                                                    )}
                                                    {selectedPayslip?.overtime_2_0 > 0 && (
                                                        <div className="list-item">
                                                            <span>Overtime(2.0x)</span>
                                                            <span>{formatCurrency(selectedPayslip?.overtime_2_0)}</span>
                                                        </div>
                                                    )}
                                                    {selectedPayslip?.cill > 0 && (
                                                        <div className="list-item">
                                                            <span>CILL</span>
                                                            <span>{formatCurrency(selectedPayslip?.cill)}</span>
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="summary-item">
                                                    <span>Gross Salary</span>
                                                    <span>{formatCurrency(selectedPayslip?.gross_salary)}</span>
                                                </div>
                                            </div>
                                            <div className="deductions-col">
                                                <div className="section-title">Deductions</div>
                                                <div className="item-list">
                                                    <div className="list-item">
                                                        <span>NSSA</span>
                                                        <span>{formatCurrency(selectedPayslip?.nssa)}</span>
                                                    </div>
                                                    <div className="list-item">
                                                        <span>Paye</span>
                                                        <span>{formatCurrency(selectedPayslip?.paye)}</span>
                                                    </div>
                                                    <div className="list-item">
                                                        <span>Aids Levy</span>
                                                        <span>{formatCurrency(selectedPayslip?.aids_levy)}</span>
                                                    </div>
                                                    {selectedPayslip?.deduction_data?.length === 0 ? (
                                                        <div className="text-muted italic fs-12 py-2 text-center"></div>
                                                    ) : (
                                                        selectedPayslip?.deduction_data?.map((item, idx) => (
                                                            <div key={idx} className="list-item">
                                                                <span>{item.title}</span>
                                                                <span>{formatCurrency(item.amount)}</span>
                                                            </div>
                                                        ))
                                                    )}
                                                </div>
                                                <div className="summary-item">
                                                    <span>Total Deductions</span>
                                                    <span>{formatCurrency(selectedPayslip?.total_deductions)}</span>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="net-pay-row">
                                            <span className="net-pay-label">Net Payable:</span>
                                            <span className="net-pay-amount">{formatCurrency(selectedPayslip?.net_salary)}</span>
                                        </div>

                                        <div className="net-pay-words-row mt-2 text-end">
                                            <small className="text-muted fw-bold italic text-uppercase" style={{ fontSize: '10px' }}>{selectedPayslip?.net_pay_words}</small>
                                        </div>

                                        <div className="payslip-signature mt-5">
                                            <div className="signature-text">burray payroll software</div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="modal-footer border-0 px-4 pb-4 bg-light d-flex justify-content-between">

                                <button onClick={handleDownloadPDF} className="btn btn-sm btn-info d-flex align-items-center gap-2 px-3 fw-bold text-white shadow-sm">
                                    <RiDownload2Line /> Download PDF
                                </button>

                                <button type="button" className="btn btn-secondary btn-sm px-4" onClick={() => setShowModal(false)}>Close View</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <style>{`
                .printable-payslip {
                  width: 100%; max-width: 800px; margin: 0 auto; background: white; 
                  padding: 3rem; box-shadow: none; border: 1px solid #eee; position: relative;
                }
                .printable-header { text-align: center; margin-bottom: 2rem; padding-bottom: 1rem; }
                .company-name { font-size: 1.5rem; font-weight: 700; color: #2563eb; text-transform: uppercase; }
                .payslip-title { font-size: 1.2rem; font-weight: 600; color: #1e3a8a; margin-top: 0.5rem; text-transform: uppercase; letter-spacing: 2px; }
                .printable-section { margin-bottom: 2rem; }
                .section-title { font-size: 0.9rem; font-weight: 700; color: #2563eb; margin-bottom: 1rem; border-bottom: 1px solid #e5e7eb; padding-bottom: 0.5rem; text-transform: uppercase; }
                .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 0.5rem 2rem; margin-bottom: 1rem; font-size: 12px; }
                .info-grid div { display: flex; justify-content: space-between; border-bottom: 1px dashed #f0f0f0; padding: 4px 0; }
                .earnings-deductions-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 3rem; margin-bottom: 2rem; }
                .item-list { min-height: 100px; }
                .list-item { display: flex; justify-content: space-between; padding: 0.5rem 0; border-bottom: 1px solid #f1f5f9; font-size: 12px; }
                .summary-item { display: flex; justify-content: space-between; font-weight: 700; margin-top: 1rem; padding-top: 0.5rem; border-top: 1px solid #e5e7eb; font-size: 13px; }
                .net-pay-row { display: flex; justify-content: flex-end; align-items: center; gap: 2rem; font-size: 1.4rem; font-weight: 700; color: #1e3a8a; padding-top: 1rem; }
                .net-pay-amount { color: #2563eb; }
                .printable-watermark {
                  position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%) rotate(-30deg);
                  z-index: 0; pointer-events: none; opacity: 0.05;
                }
                .logo-watermark-img {
                  width: 350px; height: auto; display: block;
                }
                .signature-text { text-align: center; margin-top: 2rem; font-size: 10px; text-transform: uppercase; color: #aaa; font-weight: 500; font-style: italic; letter-spacing: 3px; }
                
                /* Table hover effect */
                .table-hover tbody tr:hover { background-color: rgba(37, 99, 235, 0.03); }
                .fs-13 { font-size: 13px; }
                .italic { font-style: italic; }
                
                @media print {
                    .modal-header, .modal-footer, .no-print { display: none !important; }
                    .modal-content { border: none !important; box-shadow: none !important; }
                    .printable-payslip { border: none !important; padding: 0 !important; }
                }
            `}</style>
        </div>
    );
};

export default MyPayslips;